/**
 * Detects whether this client is running in a preview deployment.
 *
 * The flag is set by storefront/admin entry files (main.tsx) with:
 *   (globalThis as any).__TENANT_PREVIEW_MODE__ = import.meta.env.VITE_PREVIEW_MODE === 'true'
 *
 * This indirection keeps `import.meta` out of shared/ — backend (CommonJS)
 * and Node tooling import this module too, where `import.meta` is invalid.
 */
function isPreviewMode() {
    return globalThis.__TENANT_PREVIEW_MODE__ === true;
}
/**
 * Returns the browser's current Host so the backend's resolveTenant middleware
 * can find the right TenantDomain row.
 *
 * Why we need this: in production the storefront origin (e.g.
 * tenant-proj-storefront.pages.dev) and the backend origin (e.g.
 * tenant-proj-backend.onrender.com) live on different hosts. A vanilla
 * browser fetch cannot set the `Host` header (it's a forbidden header) and
 * the backend, by default, sees its OWN hostname in `Host`. Since the API
 * hostname is not a TenantDomain entry, resolveTenant returns 404.
 *
 * The backend's middleware/tenantContext.ts already prefers `X-Forwarded-Host`
 * over `Host` (extractHost()), and the corsConfig allowlists this header. So
 * by appending it from every browser-initiated request we restore the correct
 * tenant lookup without requiring a reverse proxy in front of the API.
 *
 * Returns undefined in non-browser environments (SSR, tests, Capacitor pre-init)
 * so callers can spread it with `...(value && { 'X-Forwarded-Host': value })`.
 */
function browserForwardedHost() {
    const w = globalThis.location;
    return w?.host || undefined;
}
class ApiClient {
    constructor(baseURL) {
        this.token = null;
        this.tenantIdOverride = null;
        this.unauthorizedFiredAt = 0;
        this.auth = {
            login: (credentials) => this.request('/auth/login', {
                method: 'POST',
                body: credentials
            }),
            register: (userData) => this.request('/auth/register', {
                method: 'POST',
                body: userData
            }),
            me: () => this.request('/auth/me')
        };
        this.admin = {
            getOrders: (params) => this.get('/admin/orders', { params }),
            updateOrderStatus: (orderId, status, notes, photo) => this.request(`/admin/orders/${orderId}/status`, {
                method: 'PUT',
                body: { status, notes, photo }
            }),
            assignUser: (orderId, payload) => this.request(`/admin/orders/${orderId}/assign`, {
                method: 'POST',
                body: payload
            }),
            getUsers: (params) => this.request('/admin/users' + (params ? '?' + new URLSearchParams(params).toString() : '')),
            getStats: () => this.request('/admin/stats'),
            getNavigationCounts: () => this.request('/admin/navigation/counts'),
            // =============== УПРАВЛЕНИЕ ЦВЕТАМИ ===============
            flowers: {
                getAll: (params) => this.request('/admin/flowers' + (params ? '?' + new URLSearchParams(params).toString() : '')),
                getById: (id) => this.request(`/admin/flowers/${id}`),
                create: (data) => this.request('/admin/flowers', {
                    method: 'POST',
                    body: data
                }),
                update: (id, data) => this.request(`/admin/flowers/${id}`, {
                    method: 'PUT',
                    body: data
                }),
                delete: (id) => this.request(`/admin/flowers/${id}`, {
                    method: 'DELETE'
                }),
                updateStock: (id, quantity) => this.request(`/admin/flowers/${id}/stock`, {
                    method: 'PUT',
                    body: { quantity }
                })
            },
            // =============== УПРАВЛЕНИЕ БУКЕТАМИ ===============
            bouquets: {
                getAll: (params) => this.request('/admin/bouquets' + (params ? '?' + new URLSearchParams(params).toString() : '')),
                getById: (id) => this.request(`/admin/bouquets/${id}`),
                create: (data) => this.request('/admin/bouquets', {
                    method: 'POST',
                    body: data
                }),
                update: (id, data) => this.request(`/admin/bouquets/${id}`, {
                    method: 'PUT',
                    body: data
                }),
                delete: (id) => this.request(`/admin/bouquets/${id}`, {
                    method: 'DELETE'
                })
            },
            // =============== УПРАВЛЕНИЕ КАТЕГОРИЯМИ ===============
            categories: {
                getAll: () => this.request('/admin/categories'),
                getById: (id) => this.request(`/admin/categories/${id}`),
                create: (data) => this.request('/admin/categories', {
                    method: 'POST',
                    body: data
                }),
                update: (id, data) => this.request(`/admin/categories/${id}`, {
                    method: 'PUT',
                    body: data
                }),
                delete: (id) => this.request(`/admin/categories/${id}`, {
                    method: 'DELETE'
                }),
                getBouquets: (id) => this.request(`/admin/categories/${id}/bouquets`),
                updateBouquets: (id, bouquetIds) => this.request(`/admin/categories/${id}/bouquets`, {
                    method: 'PUT',
                    body: { bouquetIds }
                })
            },
            // =============== УПРАВЛЕНИЕ ЧАТАМИ ===============
            chats: {
                getAll: (params) => this.request('/admin/chats' + (params ? '?' + new URLSearchParams(params).toString() : '')),
                getById: (id) => this.request(`/admin/chats/${id}`),
                getMessages: (chatId, params) => this.request(`/admin/chats/${chatId}/messages` + (params ? '?' + new URLSearchParams(params).toString() : '')),
                sendMessage: (chatId, content, type = 'text') => this.request(`/admin/chats/${chatId}/messages`, {
                    method: 'POST',
                    body: { content, type }
                }),
                updateStatus: (chatId, payload) => this.request(`/admin/chats/${chatId}/status`, {
                    method: 'PUT',
                    body: payload
                }),
                markAsRead: (chatId) => this.request(`/admin/chats/${chatId}/read`, {
                    method: 'PUT'
                })
            },
            // =============== УПРАВЛЕНИЕ УВЕДОМЛЕНИЯМИ ===============
            notifications: {
                getAll: (params) => {
                    const queryParams = new URLSearchParams();
                    if (params?.page)
                        queryParams.append('page', params.page.toString());
                    if (params?.limit)
                        queryParams.append('limit', params.limit.toString());
                    if (params?.category)
                        queryParams.append('category', params.category);
                    if (params?.isRead !== undefined)
                        queryParams.append('isRead', params.isRead.toString());
                    if (params?.isUrgent !== undefined)
                        queryParams.append('isUrgent', params.isUrgent.toString());
                    const queryString = queryParams.toString();
                    return this.request('/admin/notifications' + (queryString ? `?${queryString}` : ''));
                },
                markAsRead: (notificationId) => this.request(`/admin/notifications/${notificationId}/read`, {
                    method: 'POST'
                }),
                markAllAsRead: (category) => this.request('/admin/notifications/read-all', {
                    method: 'POST',
                    body: { category }
                }),
                delete: (notificationId) => this.request(`/admin/notifications/${notificationId}`, {
                    method: 'DELETE'
                })
            }
        };
        this.bouquets = {
            getAll: (params) => this.request('/bouquets' + (params ? '?' + new URLSearchParams(params).toString() : '')),
            getById: (id) => this.request(`/bouquets/${id}`),
            getCategories: () => this.request('/bouquets/categories/all')
        };
        this.orders = {
            create: (orderData) => this.request('/orders', {
                method: 'POST',
                body: orderData
            }),
            getMyOrders: (params) => this.get('/orders', { params }),
            getTracking: (id) => this.request(`/orders/${id}/tracking`)
        };
        this.baseURL = baseURL;
    }
    setToken(token) {
        this.token = token;
    }
    /**
     * Sprint D (Master Plan v5.1, rule #4): set explicit X-Tenant-Id header on
     * every subsequent request. Used by admin running on shared admin domain
     * where the active tenant is selected via UI dropdown rather than Host.
     *
     * Backend (`middleware/tenantContext.ts`) accepts the header ONLY after
     * verifying that `req.user` has TenantMembership for the requested tenantId.
     * Setting an arbitrary value here does NOT bypass enforcement — backend
     * rejects with 403 if the JWT user is not a member.
     *
     * For storefront / tenant-bound admin host, leave this null — Host-based
     * resolution applies and the header is rejected if present (defense in depth).
     */
    setTenantId(tenantId) {
        this.tenantIdOverride = tenantId;
    }
    setUnauthorizedHandler(handler) {
        this.onUnauthorized = handler;
    }
    /**
     * Fetch a binary resource (e.g. PDF) using the same auth as request().
     * Returns a Blob — caller is responsible for triggering download/preview.
     */
    async fetchBlob(endpoint) {
        const url = `${this.baseURL}${endpoint}`;
        const fwdHost = browserForwardedHost();
        const headers = {
            ...(this.token && { Authorization: `Bearer ${this.token}` }),
            ...(this.tenantIdOverride && { 'X-Tenant-Id': this.tenantIdOverride }),
            ...(isPreviewMode() && { 'X-Preview-Mode': 'true' }),
            // Forward the storefront's host so backend.resolveTenant can find the
            // TenantDomain row instead of falling back to its own API hostname.
            ...(fwdHost && { 'X-Forwarded-Host': fwdHost }),
            // Wave 3 D2 Phase 1: signal accept canonical UPPER wire format
            // (consistent with request() above — blob endpoints don't carry
            // OrderStatus today, but observability + future-proofing require it).
            'X-API-Version': '2',
        };
        const response = await fetch(url, { headers });
        if (!response.ok) {
            const err = new Error(`HTTP ${response.status}`);
            err.response = { status: response.status, statusText: response.statusText };
            throw err;
        }
        // Try to parse filename from Content-Disposition: inline; filename="..."
        const cd = response.headers.get('content-disposition') || '';
        const m = cd.match(/filename="?([^";]+)"?/i);
        const filename = m ? m[1] : null;
        const blob = await response.blob();
        return { blob, filename };
    }
    /**
     * Sprint H Phase 3 (rule §H-3): public typed wrapper around the protected
     * `request` method. Used by hooks and one-off call sites that don't fit a
     * domain method but MUST still receive Authorization, X-Tenant-Id, 401
     * handler, and uniform error shape.
     *
     * NOT for external presigned URLs (R2/S3) — those must NOT carry
     * Authorization. Use raw `fetch` with an inline eslint-disable comment
     * for those exceptions.
     */
    async requestRaw(endpoint, options = {}) {
        return this.request(endpoint, options);
    }
    /**
     * Upload a raw binary payload (e.g. image/video buffer) to a backend
     * endpoint. Bypasses JSON serialization but preserves every tenant-resolution
     * header that `request()` normally adds:
     *
     *   - Authorization (Bearer)
     *   - X-Tenant-Id   (only if setTenantId() was called — shared admin host)
     *   - X-Forwarded-Host (browser host so backend.resolveTenant maps to the
     *                      right TenantDomain row; without this the backend
     *                      sees its own API hostname → 404 not_found on
     *                      /media/upload-proxy and similar non-/api/admin
     *                      routes that fall into resolveTenant branch (a))
     *   - X-Preview-Mode  (build-time flag, mirrors request())
     *   - X-API-Version   (Wave 3 D2 wire-format negotiation)
     *
     * The Content-Type is set from the `contentType` argument (e.g. `image/jpeg`)
     * — callers that need `application/octet-stream` semantics pass that in
     * directly. We do NOT default to JSON here, because that's the whole point.
     *
     * Why this exists: prior to Sprint H Phase 3 there were three call sites
     * (`uploadToR2`, `UniversalMediaUploader`, `FlowerVisionMatchPage`) that
     * dropped down to raw `fetch` for binary uploads and each one independently
     * forgot X-Forwarded-Host. Production admin on a shared admin host then got
     * 404 from resolveTenant on `/api/media/upload-proxy`. Centralising here
     * lets any future binary-upload caller inherit the correct headers.
     */
    async uploadBinary(endpoint, body, contentType, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        const fwdHost = browserForwardedHost();
        const headers = {
            'Content-Type': contentType,
            ...(this.token && { Authorization: `Bearer ${this.token}` }),
            ...(this.tenantIdOverride && { 'X-Tenant-Id': this.tenantIdOverride }),
            ...(isPreviewMode() && { 'X-Preview-Mode': 'true' }),
            ...(fwdHost && { 'X-Forwarded-Host': fwdHost }),
            'X-API-Version': '2',
            ...(options.extraHeaders ?? {}),
        };
        const init = {
            method: 'POST',
            headers,
            body: body,
        };
        if (options.signal)
            init.signal = options.signal;
        const response = await fetch(url, init);
        if (!response.ok) {
            const errorData = await response
                .json()
                .catch(() => ({ error: response.statusText }));
            const error = new Error(errorData.error?.message ||
                errorData.message ||
                errorData.error ||
                `HTTP ${response.status}`);
            error.response = {
                status: response.status,
                statusText: response.statusText,
                data: errorData,
            };
            throw error;
        }
        if (response.status === 204 || response.headers.get('content-length') === '0') {
            return undefined;
        }
        const text = await response.text();
        if (!text)
            return undefined;
        try {
            return JSON.parse(text);
        }
        catch {
            return undefined;
        }
    }
    async request(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        const fwdHost = browserForwardedHost();
        const config = {
            method: options.method || 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...(this.token && { Authorization: `Bearer ${this.token}` }),
                // Sprint D (rule #4): include X-Tenant-Id only if admin explicitly set
                // an override via setTenantId() (shared admin host workflow). On
                // storefront / tenant-bound admin host this stays null and backend
                // rejects the header if present.
                ...(this.tenantIdOverride && { 'X-Tenant-Id': this.tenantIdOverride }),
                // Preview-mode signal: read once at module init from build-time env.
                // Backend rejects mutations carrying this header (defense in depth —
                // header is also inert in production NODE_ENV).
                ...(isPreviewMode() && { 'X-Preview-Mode': 'true' }),
                // Forward the storefront's host so backend.resolveTenant maps the
                // request to the right TenantDomain row. Without this header the
                // backend sees its own API hostname (e.g. *.onrender.com) in `Host`,
                // which is never a tenant entry → public routes 404 with not_found.
                // The corsConfig.allowedHeaders list includes X-Forwarded-Host so
                // browser preflight passes.
                ...(fwdHost && { 'X-Forwarded-Host': fwdHost }),
                // Wave 3 D2 Phase 1: signal that the client accepts canonical UPPER
                // wire format. Backend currently still serializes kebab by default;
                // this header lets us flip the default in Phase 2 without coordinating
                // a frontend redeploy. All three frontends (storefront/floristy/admin)
                // already accept-both at their repository edges via parseOrderStatus.
                'X-API-Version': '2',
                ...options.headers
            }
        };
        if (options.signal) {
            config.signal = options.signal;
        }
        if (options.body) {
            config.body = typeof options.body === 'string' ? options.body : JSON.stringify(options.body);
        }
        try {
            const response = await fetch(url, config);
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: response.statusText }));
                // Handle 401 Unauthorized - trigger logout (but NOT for auth endpoints)
                if (response.status === 401) {
                    // Skip unauthorized handler for login/register endpoints
                    // 401 on these endpoints means "wrong credentials", not "session expired"
                    const isAuthEndpoint = endpoint.includes('/auth/login') || endpoint.includes('/auth/register');
                    const requestAuthorization = config.headers?.Authorization;
                    const isGuestSessionRequest = typeof requestAuthorization === 'string' && requestAuthorization.startsWith('Bearer gs_');
                    if (!isAuthEndpoint && !isGuestSessionRequest) {
                        // CRITICAL: Clear token SYNCHRONOUSLY before handler fires.
                        // This prevents any in-flight or queued requests from retrying
                        // with the same dead token (race condition root cause).
                        this.token = null;
                        const now = Date.now();
                        // Debounce: fire handler at most once per 2 seconds to avoid
                        // repeated logout/redirect from concurrent 401 responses
                        if (this.onUnauthorized && now - this.unauthorizedFiredAt > 2000) {
                            this.unauthorizedFiredAt = now;
                            console.warn('[ApiClient] 401 Unauthorized:', errorData?.error || errorData?.message || 'no detail from server', { endpoint });
                            this.onUnauthorized();
                        }
                    }
                    // Preserve backend error message and attach status so React Query
                    // retry logic can detect 401 without relying on the ApiError class
                    const authError = new Error(errorData.error || errorData.message || 'Authentication required');
                    authError.response = { status: 401, statusText: 'Unauthorized', data: errorData };
                    throw authError;
                }
                // ⚠️ FIX: Сохраняем полную структуру error.response для RateLimitError handling
                const error = new Error(errorData.error?.message || errorData.message || errorData.error || `HTTP ${response.status}`);
                error.response = {
                    status: response.status,
                    statusText: response.statusText,
                    data: errorData
                };
                throw error;
            }
            // Handle 204 No Content - return null/undefined as T
            if (response.status === 204 || response.headers.get('content-length') === '0') {
                return undefined;
            }
            // Try to parse JSON, but handle empty responses gracefully
            const text = await response.text();
            if (!text) {
                return undefined;
            }
            try {
                return JSON.parse(text);
            }
            catch (error) {
                console.warn('[ApiClient] Failed to parse JSON response:', error);
                return undefined;
            }
        }
        catch (error) {
            if (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') {
                throw error;
            }
            const errorMessage = error instanceof Error
                ? error.message
                : typeof error === 'string'
                    ? error
                    : '';
            const isExpectedIdempotentError = errorMessage.includes('Resource already exists') || errorMessage.includes('409');
            if (!isExpectedIdempotentError) {
                console.error('API Request failed:', error);
            }
            throw error;
        }
    }
    // =============== ПУБЛИЧНЫЕ HTTP МЕТОДЫ ===============
    // Добавлены для прямого использования в хуках и сервисах
    /**
     * Выполнить GET-запрос
     * @param endpoint - URL endpoint (например, '/admin/florist/pending-orders')
     * @param options - Опции запроса (params для query string, signal для AbortController)
     * @returns Promise с типизированным ответом
     */
    get(endpoint, options) {
        let url = endpoint;
        if (options?.params) {
            // Фильтруем undefined/null и поддерживаем массивы как repeated params: ?status=A&status=B
            const queryParams = new URLSearchParams();
            for (const [key, value] of Object.entries(options.params)) {
                if (value === undefined || value === null)
                    continue;
                if (Array.isArray(value)) {
                    for (const item of value) {
                        if (item !== undefined && item !== null) {
                            queryParams.append(key, String(item));
                        }
                    }
                }
                else {
                    queryParams.set(key, String(value));
                }
            }
            const queryString = queryParams.toString();
            if (queryString) {
                url = `${endpoint}?${queryString}`;
            }
        }
        return this.request(url, {
            method: 'GET',
            signal: options?.signal
        });
    }
    /**
     * Выполнить POST-запрос
     * @param endpoint - URL endpoint
     * @param data - Данные для отправки в body
     * @param options - Опции запроса (signal для AbortController)
     * @returns Promise с типизированным ответом
     */
    post(endpoint, data, options) {
        return this.request(endpoint, {
            method: 'POST',
            body: data,
            signal: options?.signal
        });
    }
    /**
     * Выполнить PUT-запрос
     * @param endpoint - URL endpoint
     * @param data - Данные для отправки в body
     * @param options - Опции запроса (signal для AbortController)
     * @returns Promise с типизированным ответом
     */
    put(endpoint, data, options) {
        return this.request(endpoint, {
            method: 'PUT',
            body: data,
            signal: options?.signal
        });
    }
    /**
     * Выполнить PATCH-запрос
     * @param endpoint - URL endpoint
     * @param data - Данные для отправки в body
     * @param options - Опции запроса (signal для AbortController)
     * @returns Promise с типизированным ответом
     */
    patch(endpoint, data, options) {
        return this.request(endpoint, {
            method: 'PATCH',
            body: data,
            signal: options?.signal
        });
    }
    /**
     * Выполнить DELETE-запрос
     * @param endpoint - URL endpoint
     * @param options - Опции запроса (signal для AbortController)
     * @returns Promise с типизированным ответом
     */
    delete(endpoint, options) {
        return this.request(endpoint, {
            method: 'DELETE',
            signal: options?.signal
        });
    }
}
// Экспортируем только класс - каждое приложение создает свой экземпляр
export { ApiClient };
